import React, { createContext, useState, useCallback, useContext } from 'react';
import api from '../../services/api';
import UserStatuses from '../../enums/UserStatuses';
import AccessibilityScript from '../../accessibility/AccessibilityScript';

const AuthContext = createContext();

function AuthProvider({ children }) {
    const [loading, setLoading] = useState(null);

    const toggleLoading = useCallback((state) => {
        setLoading(state);
    }, []);

    const [giftId, setGiftId] = useState(null);
    const setGlobalGift = useCallback(
        (id) => {
            setGiftId(id);
        },
        [setGiftId]
    );
    const getGlobalGift = useCallback(() => {
        return giftId;
    }, [giftId]);

    const [currentScript, setCurrentScript] = useState(null);

    const [globalUpdate, setGlobalUpdate] = useState({});

    const [accessibilityOptions, setAccessibilityOptions] = useState({});
    const minFontSize = 13;
    const maxFontSize = 20;

    const [authenticated, setAuthenticated] = useState(() => {
        const user = localStorage.getItem('@Reachout:user');
        if (user) {
            const userData = JSON.parse(user);
            api.defaults.headers.authorization = `${userData.token}`;
            api.defaults.headers.email_authorization = userData.email;
            toggleLoading(false);
            return { user: userData };
        }
    });
    const signIn = useCallback(async ({ email, password }) => {
        try {
            const response = await api.post('ro.team.signin', {
                email,
                password,
            });
            if (response.data.status.code !== UserStatuses.CONFIRMED) {
                return Promise.reject({msg: 'Please, confirm your email', code: 460});
            }

            const { data } = response;
            if (data.role === 1) { data.role = 'coordinator'; }
            else if (data.role === 2) { data.role = 'giver'; }
            else if (data.role === 3) { data.role = 'receiver'; }
            localStorage.setItem('@Reachout:user', JSON.stringify(data));
            api.defaults.headers.authorization = `${data.token}`;
            api.defaults.headers.email_authorization = data.email;
            setAuthenticated(data);
        } catch (err) {
            if (err.response.data.message) {
                return Promise.reject(err.response.data.message);
            } else {
                return Promise.reject('General error');
            }
        }
    }, []);

    const logout = useCallback(async () => {
        setAuthenticated();
        localStorage.removeItem('@Reachout:user');
        localStorage.removeItem('@Reachout:group');
        localStorage.removeItem('@Reachout:groupRole');
    }, []);

    const [currentGroup, setCurrentGroup] = useState(() => {
        const group = localStorage.getItem('@Reachout:group');
        if (group) {
            const groupData = JSON.parse(group);
            return groupData;
        }
    });
    const storeCurrentGroup = useCallback(
        (group) => {
            localStorage.setItem('@Reachout:group', JSON.stringify(group));
            setCurrentGroup(group);
        },
        [setCurrentGroup]
    );
    const removeCurrentGroup = useCallback(() => {
        setCurrentGroup();
        localStorage.removeItem('@Reachout:group');
    }, [setCurrentGroup]);

    const loadAccessibilityScript = useCallback((callback) => {
        const existingScript = document.getElementById('accessibilityScript');
      
        if (!existingScript) {
          const script = document.createElement('script');
        //   script.src = process.env.REACT_APP_ACCESSIBILITY_SCRIPT_URL;
          script.id = 'accessibilityScript';
          script.innerHTML = `${AccessibilityScript}`;
          document.body.appendChild(script);
      
          script.onload = () => {
            if (callback) callback();
          };
        }
      
        if (existingScript && callback) callback();
    }, []);

    const changeFontSize = useCallback((e) => {
        let bodyFontSize = parseFloat(getComputedStyle(document.body).fontSize);
        let htmlFontSize = parseFloat(getComputedStyle(document.getElementsByTagName('html')[0]).fontSize);

        if (e === '+') {
            bodyFontSize ++;
            htmlFontSize ++;
        } else if (e === '-') {
            bodyFontSize --;
            htmlFontSize --;
        }

        if (bodyFontSize < minFontSize || bodyFontSize > maxFontSize) {
            return false;
        }

        const body = document.getElementsByTagName('body');
        body[0].style.fontSize = bodyFontSize + "px";
        const html = document.getElementsByTagName('html');
        html[0].style.fontSize = htmlFontSize + "px";

        localStorage.setItem(
            '@Reachout:accessibility',
            JSON.stringify({...accessibilityOptions, bodyFontSize, htmlFontSize})
        );
        setAccessibilityOptions({...accessibilityOptions, bodyFontSize, htmlFontSize});
    }, [setAccessibilityOptions, accessibilityOptions]);

    const loadFontSize = useCallback(() => {
        
        let currentAccessibility = localStorage.getItem('@Reachout:accessibility');
        if (currentAccessibility && currentAccessibility !== undefined) {
            currentAccessibility = JSON.parse(currentAccessibility);
            // FontSize
            if (currentAccessibility.bodyFontSize !== undefined) {
                const currentBodyFontSize = parseFloat(getComputedStyle(document.body).fontSize);
                if (currentAccessibility.bodyFontSize !== currentBodyFontSize) {
                    const body = document.getElementsByTagName('body');
                    body[0].style.fontSize = currentAccessibility.bodyFontSize + "px";
                }
            }
            if (currentAccessibility.htmlFontSize !== undefined) {
                const currentHtmlFontSize = parseFloat(getComputedStyle(document.getElementsByTagName('html')[0]).fontSize);
                if (currentAccessibility.htmlFontSize !== currentHtmlFontSize) {
                    const html = document.getElementsByTagName('html');
                    html[0].style.fontSize = currentAccessibility.htmlFontSize + "px";
                }
            }
        }
    }, []);

    return (
        <AuthContext.Provider
            value={{
                authenticated,
                signIn,
                logout,
                toggleLoading,
                loading,
                currentGroup,
                storeCurrentGroup,
                removeCurrentGroup,
                setGlobalGift,
                getGlobalGift,
                globalUpdate,
                setGlobalUpdate,
                currentScript,
                setCurrentScript,
                loadAccessibilityScript,
                loadFontSize,
                changeFontSize
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

function useAuth() {
    const context = useContext(AuthContext);

    return context;
}

export { useAuth, AuthProvider };
